import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { PeoplePicker, type PeoplePickerProfile } from '@/components/projects/PeoplePicker';
import { useProfiles } from '@/features/users/hooks';
import {
  useCreateTicket,
  useTicketCategories,
  type TicketCategory,
} from '@/features/tickets/hooks';
import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_META,
  describeTicketError,
  ticketCategoryIcon,
  ticketRef,
} from '@/features/tickets/model';
import type { TicketPriority } from '@/types/database';

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface NewTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select a category (e.g. from an empty-state shortcut). */
  initialCategoryId?: string | null;
  /** Called with the new ticket id so the page can open it. */
  onCreated?: (ticketId: string) => void;
}

/**
 * "New ticket" — a request, not an order. Pick what kind of thing it
 * is, say what you need and (optionally) who should hear about it. If
 * you leave "Send to" empty the category's default routing applies, or
 * the triage tier picks it up from the inbox.
 */
export function NewTicketDialog({
  open,
  onOpenChange,
  initialCategoryId = null,
  onCreated,
}: NewTicketDialogProps) {
  const { profile, role } = useAuth();
  const canTriage = role === 'super_admin' || role === 'admin' || role === 'manager';
  const categoriesQ = useTicketCategories();
  const profilesQ = useProfiles();
  const create = useCreateTicket();

  const [categoryId, setCategoryId] = useState<string | null>(initialCategoryId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [neededBy, setNeededBy] = useState('');
  const [sendTo, setSendTo] = useState<string[]>([]);
  const [onBehalfOf, setOnBehalfOf] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setCategoryId(initialCategoryId);
      setTitle('');
      setDescription('');
      setPriority('medium');
      setNeededBy('');
      setSendTo([]);
      setOnBehalfOf([]);
      setFiles([]);
    }
  }, [open, initialCategoryId]);

  const people: PeoplePickerProfile[] = useMemo(
    () =>
      (profilesQ.data ?? [])
        .filter((p) => p.is_active && p.role !== 'external_lender')
        .map((p) => ({
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          avatar_url: p.avatar_url,
        })),
    [profilesQ.data],
  );

  const categories = categoriesQ.data ?? [];
  const selectedCategory = categories.find((c) => c.id === categoryId) ?? null;
  const defaultAssignee = selectedCategory?.default_assignee_id
    ? people.find((p) => p.id === selectedCategory.default_assignee_id) ?? null
    : null;

  const canSubmit = title.trim().length > 0 && !create.isPending;

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next: File[] = [];
    for (const f of Array.from(list)) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`"${f.name}" is over the 25 MB limit.`);
        continue;
      }
      next.push(f);
    }
    setFiles((prev) => [...prev, ...next]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !profile) return;
    try {
      const { ticket, attachmentFailures } = await create.mutateAsync({
        title,
        description,
        categoryId,
        priority,
        neededBy: neededBy || null,
        assigneeId: sendTo[0] ?? null,
        requesterId: canTriage && onBehalfOf[0] ? onBehalfOf[0] : null,
        files,
      });
      const routedTo = ticket.assignee ? ticket.assignee.full_name ?? ticket.assignee.email : null;
      toast.success(
        routedTo
          ? `${ticketRef(ticket.number)} sent to ${routedTo}.`
          : `${ticketRef(ticket.number)} filed — a manager will route it.`,
      );
      for (const f of attachmentFailures) toast.error(`Attachment failed — ${f}`);
      onOpenChange(false);
      onCreated?.(ticket.id);
    } catch (err) {
      toast.error(describeTicketError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>New ticket</DialogTitle>
            <DialogDescription>
              Ask for something — a tool, an order, access, a repair, an idea. Whoever
              receives it will route it and you will see every status change.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label>What kind of request is this?</Label>
            <CategoryGrid
              categories={categories}
              value={categoryId}
              onChange={setCategoryId}
              loading={categoriesQ.isLoading}
            />
            {selectedCategory?.description && (
              <p className="text-xs text-muted-foreground">{selectedCategory.description}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ticket-title">What do you need?</Label>
            <Input
              id="ticket-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. New 10 mm drill bit for the CNC station"
              maxLength={160}
              autoFocus
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ticket-description">Details</Label>
            <Textarea
              id="ticket-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Anything that helps: where, why, which part, how many, links…"
              className="min-h-[88px]"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                <SelectTrigger className="h-10 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden
                          className={cn('h-2 w-2 rounded-full', TICKET_PRIORITY_META[p].dot)}
                        />
                        {TICKET_PRIORITY_META[p].label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Needed by</Label>
              <DatePicker
                value={neededBy}
                onChange={setNeededBy}
                placeholder="No date"
                aria-label="Needed by"
                className="h-10 w-full"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Send to</Label>
            <PeoplePicker
              profiles={people}
              value={sendTo}
              onChange={(next) => setSendTo(next.slice(-1))}
              placeholder={
                defaultAssignee
                  ? `Goes to ${defaultAssignee.full_name ?? defaultAssignee.email} unless you pick someone`
                  : 'Leave empty and a manager will route it'
              }
            />
            <p className="text-xs text-muted-foreground">
              One person. They can hand it to someone else, ask for more detail, or
              decline it with a reason — a ticket is a request, not an order.
            </p>
          </div>

          {canTriage && (
            <div className="space-y-2">
              <Label>On behalf of</Label>
              <PeoplePicker
                profiles={people.filter((p) => p.id !== profile?.id)}
                value={onBehalfOf}
                onChange={(next) => setOnBehalfOf(next.slice(-1))}
                placeholder="Filing for someone else? Pick them"
              />
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Attachments</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-3.5 w-3.5" />
                Add file
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
            </div>
            {files.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {files.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-secondary/50 py-0.5 pl-2.5 pr-1 text-xs"
                  >
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                      className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter className="mt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Send request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CategoryGrid({
  categories,
  value,
  onChange,
  loading,
  compact,
}: {
  categories: Array<Pick<TicketCategory, 'id' | 'slug' | 'name'>>;
  value: string | null;
  onChange: (id: string | null) => void;
  loading?: boolean;
  /** Tighter tiles for the tablet. */
  compact?: boolean;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg bg-secondary/60" />
        ))}
      </div>
    );
  }
  return (
    <div
      className={cn(
        'grid gap-2',
        compact ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3',
      )}
      role="radiogroup"
      aria-label="Category"
    >
      {categories.map((c) => {
        const Icon = ticketCategoryIcon(c.slug);
        const active = value === c.id;
        return (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(active ? null : c.id)}
            className={cn(
              'flex min-h-[52px] items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
              active
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-card text-foreground hover:bg-secondary/60',
            )}
          >
            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.7} />
            <span className="min-w-0 truncate">{c.name}</span>
          </button>
        );
      })}
    </div>
  );
}
